// pages/admin/AdminRewards.tsx

import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  useMediaQuery,
  useTheme,
  Drawer,
  CircularProgress,
  Alert,
  Button,
  TextField,
  Avatar,
  Breadcrumbs,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  InputAdornment,
  Chip,
  Paper,
} from '@mui/material';
import {
  Refresh,
  NavigateNext,
  Home,
  Search,
  Person,
  Stars,
  Email,
  CalendarToday,
  EmojiEvents,
} from '@mui/icons-material';

import Slidemenu from '../../components/admin/adminSlidemenu.tsx';
import AdminHeader from '../../components/admin/adminHeader.tsx';

import {
  useListAdminRewardsQuery,
  UserReward,
} from '../../features/admin/adminRewardsApi.ts';

// Shopify-like color palette
const shopifyColors = {
  primary: '#008060',
  primaryLight: '#00a47c',
  primaryDark: '#006e52',
  background: '#f6f6f7',
  surface: '#ffffff',
  border: '#e1e3e5',
  textPrimary: '#202223',
  textSecondary: '#6d7175',
  success: '#008060',
  warning: '#ffc453',
  error: '#d72c0d',
  blue: '#2c6ecb',
  purple: '#5c6ac4',
  gold: '#ffd700',
};

const AdminRewards = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchStr, setSearchStr] = useState('');

  // API hooks
  const {
    data,
    isLoading,
    error,
    isError,
    refetch
  } = useListAdminRewardsQuery();

  // Handle API response structure
  const userRewards = data?.rewards || [];
  const totalRecords = data?.total || 0;

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleRefresh = () => {
    refetch();
  };

  const renderErrorMessage = () => {
    if (error && 'status' in error) {
      switch (error.status) {
        case 403:
          return 'You don\'t have permission to view user rewards.';
        case 401:
          return 'Please sign in to continue.';
        case 404:
          return 'No user rewards found.';
        case 500:
          return 'Something went wrong. Please try again.';
        default:
          return 'Unable to load user rewards. Please try again.';
      }
    }
    return 'Network error. Please check your connection.';
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  // Filter rewards based on search
  const filteredRewards = userRewards.filter((reward: UserReward) => {
    const matchesSearch = !searchStr || 
      reward.name?.toLowerCase().includes(searchStr.toLowerCase()) ||
      reward.user_email?.toLowerCase().includes(searchStr.toLowerCase()) ||
      reward.points?.toLowerCase().includes(searchStr.toLowerCase()) ||
      reward.user_id?.toString().includes(searchStr);
    
    return matchesSearch;
  });

  const getPointsColor = (points: string) => {
    const pointValue = parseFloat(points);
    if (pointValue >= 50) return shopifyColors.success;
    if (pointValue >= 20) return shopifyColors.warning;
    return shopifyColors.textSecondary;
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', backgroundColor: shopifyColors.background }}>
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* Sidebar */}
        <Box sx={{ width: '250px', flexShrink: 0, display: { xs: 'none', md: 'block' } }}>
          <Slidemenu mobileOpen={false} onToggle={handleDrawerToggle} />
        </Box>

        {/* Drawer for mobile */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { width: 250 },
          }}
        >
          <Slidemenu onToggle={handleDrawerToggle} mobileOpen={false} />
        </Drawer>

        {/* Main Content */}
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <AdminHeader onToggleMenu={handleDrawerToggle} />
          
          {/* Page Header */}
          <Box sx={{ p: 3, pb: 2, backgroundColor: shopifyColors.surface }}>
            <Breadcrumbs separator={<NavigateNext fontSize="small" />} sx={{ mb: 2 }}>
              <Link color="inherit" href="#" underline="hover">
                <Home sx={{ fontSize: 20 }} />
              </Link>
              <Link color="inherit" href="#" underline="hover">
                Users
              </Link>
              <Typography color={shopifyColors.textPrimary} sx={{ fontWeight: 600 }}>
                User Rewards
              </Typography>
            </Breadcrumbs>
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
              <Box>
                <Typography variant="h4" sx={{ 
                  fontWeight: 700, 
                  color: shopifyColors.textPrimary,
                  mb: 1
                }}>
                  User Rewards
                </Typography>
                <Typography variant="body1" sx={{ color: shopifyColors.textSecondary }}>
                  View all user reward points and activity
                </Typography>
              </Box>
              
              {/* Stats Card */}
              <Paper
                elevation={0}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  px: 3,
                  py: 2,
                  backgroundColor: `${shopifyColors.primary}10`,
                  borderRadius: '8px',
                  border: `1px solid ${shopifyColors.primary}30`,
                }}
              >
                <EmojiEvents sx={{ fontSize: 32, color: shopifyColors.gold }} />
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: shopifyColors.textPrimary }}>
                    {totalRecords}
                  </Typography>
                  <Typography variant="caption" sx={{ color: shopifyColors.textSecondary }}>
                    Total Users
                  </Typography>
                </Box>
              </Paper>
            </Box>
          </Box>

          {/* Main Content Area */}
          <Box sx={{ flexGrow: 1, p: 3, pt: 2, overflow: 'auto' }}>
            {/* Filter Card */}
            <Card sx={{ mb: 3, borderRadius: '8px', border: `1px solid ${shopifyColors.border}` }}>
              <CardContent sx={{ p: '16px !important' }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                  {/* Search */}
                  <TextField
                    size="small"
                    placeholder="Search by name, email, or points..."
                    value={searchStr}
                    onChange={(e) => setSearchStr(e.target.value)}
                    sx={{
                      minWidth: 300,
                      flexGrow: 1,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '6px',
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search fontSize="small" sx={{ color: shopifyColors.textSecondary }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                  
                  <Button
                    variant="outlined"
                    startIcon={<Refresh />}
                    onClick={handleRefresh}
                    disabled={isLoading}
                    size="small"
                    sx={{
                      borderRadius: '6px',
                      textTransform: 'none',
                      borderColor: shopifyColors.border,
                      color: shopifyColors.textPrimary,
                    }}
                  >
                    Refresh
                  </Button>
                </Box>
              </CardContent>
            </Card>

            {/* Results Summary */}
            {!isLoading && !isError && (
              <Typography variant="body2" sx={{ color: shopifyColors.textSecondary, mb: 2 }}>
                {filteredRewards.length} user{filteredRewards.length === 1 ? '' : 's'}
                {searchStr && ` matching "${searchStr}"`}
              </Typography>
            )}

            {/* Loading State */}
            {isLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                <CircularProgress sx={{ color: shopifyColors.primary }} />
                <Typography sx={{ ml: 2, color: shopifyColors.textSecondary }}>Loading user rewards...</Typography>
              </Box>
            )}

            {/* Error State */}
            {isError && !isLoading && (
              <Alert 
                severity="error" 
                sx={{ 
                  mb: 2,
                  borderRadius: '6px',
                  border: `1px solid ${shopifyColors.error}20`,
                }}
                action={
                  <Button color="inherit" size="small" onClick={handleRefresh}>
                    Try again
                  </Button>
                }
              >
                {renderErrorMessage()}
              </Alert>
            )}

            {/* Empty State */}
            {!isLoading && !isError && filteredRewards.length === 0 && (
              <Card sx={{ textAlign: 'center', py: 8, borderRadius: '8px' }}>
                <EmojiEvents sx={{ fontSize: 64, color: shopifyColors.textSecondary, mb: 2, opacity: 0.5 }} />
                <Typography variant="h6" sx={{ color: shopifyColors.textPrimary, mb: 1, fontWeight: 600 }}>
                  {searchStr ? 'No users found' : 'No user rewards yet'}
                </Typography>
                <Typography variant="body2" sx={{ color: shopifyColors.textSecondary }}>
                  {searchStr 
                    ? `No users match your search "${searchStr}"`
                    : 'User reward data will appear here once users earn points'
                  }
                </Typography>
              </Card>
            )}

            {/* User Rewards List */}
            {!isLoading && filteredRewards.length > 0 && (
              <Card sx={{ borderRadius: '8px', border: `1px solid ${shopifyColors.border}`, overflow: 'hidden' }}>
                <List sx={{ p: 0 }}>
                  {filteredRewards.map((reward: UserReward, index: number) => (
                    <ListItem
                      key={reward.id}
                      sx={{
                        borderBottom: index < filteredRewards.length - 1 ? `1px solid ${shopifyColors.border}` : 'none',
                        '&:hover': {
                          backgroundColor: `${shopifyColors.background}80`,
                        },
                        py: 2.5,
                        px: 3,
                        display: 'flex',
                        alignItems: 'flex-start',
                      }}
                    >
                      <ListItemIcon sx={{ minWidth: 56 }}>
                        <Avatar
                          sx={{
                            bgcolor: shopifyColors.blue,
                            width: 48,
                            height: 48,
                          }}
                        >
                          <Person />
                        </Avatar>
                      </ListItemIcon>
                      
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, flexWrap: 'wrap' }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: shopifyColors.textPrimary }}>
                              {reward.name || 'Unknown User'}
                            </Typography>
                            
                            <Chip
                              icon={<Stars sx={{ fontSize: 16 }} />}
                              label={`${reward.points} Points`}
                              size="small"
                              sx={{
                                borderRadius: '6px',
                                fontWeight: 600,
                                backgroundColor: `${getPointsColor(reward.points)}20`,
                                color: getPointsColor(reward.points),
                                fontSize: '0.75rem',
                              }}
                            />
                          </Box>
                        }
                        secondary={
                          <Box sx={{ mt: 1 }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Email sx={{ fontSize: 16, color: shopifyColors.textSecondary }} />
                                <Typography variant="body2" sx={{ color: shopifyColors.textSecondary }}>
                                  {reward.user_email || 'No email'}
                                </Typography>
                              </Box>
                              
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Person sx={{ fontSize: 16, color: shopifyColors.textSecondary }} />
                                <Typography variant="body2" sx={{ color: shopifyColors.textSecondary }}>
                                  User ID: {reward.user_id}
                                </Typography>
                              </Box>
                              
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CalendarToday sx={{ fontSize: 16, color: shopifyColors.textSecondary }} />
                                <Typography variant="body2" sx={{ color: shopifyColors.textSecondary }}>
                                  Updated: {formatDate(reward.updated_at)}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </Card>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default AdminRewards;